<script lang="ts">
import { LANG_EN } from '@/app/const';
import { MainTopic } from '@/interface/MainTopic.interface';
import { SubTopic } from '@/interface/SubTopic.interface';
import { complaintService, fileService } from '@/services/serviceProvider';
import airline from '@/assets/images/home/home-img-1.png';

export default {
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        mainTopic: {
            type: Object,
            default: undefined
        },
        subTopicId: {
            type: Number,
            default: undefined
        },
        subTopicDesc: {
            type: String,
            default: undefined
        },
    },
    emits: ['onConfirm', 'update:modelValue', 'updateSubTopicSelectedId'],
    data() {
        return {
            localMainTopic: this.mainTopic as MainTopic,
            subTopic: this.subTopicId as number | undefined,
            subTopicDescription: this.subTopicDesc as string | undefined,
            subTopicOptions: [] as SubTopic[],
            lang: this.$i18n.locale,
            en: LANG_EN,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            },
        }
    },
    watch: {
        async dialogShow(val) {
            if (val) {
                this.localMainTopic = this.mainTopic as MainTopic;
                if (this.localMainTopic.imageUrl && !this.localMainTopic.imageUrl.includes('/assets/')) {
                    let url = await fileService.loadImageUrl(this.localMainTopic.imageUrl);
                    this.localMainTopic.actualImageUrl = url ? url : airline;
                } else if (this.localMainTopic.imageUrl) {
                    this.localMainTopic.actualImageUrl = this.localMainTopic.imageUrl;
                } else {
                    this.localMainTopic.actualImageUrl = airline;
                }
                this.subTopic = this.subTopicId;
                this.subTopicDescription = this.subTopicDesc;
                this.getSubTopics();
            }
        },
        mainTopic: {
            async handler(newVal, oldVal) {
                if (newVal) {
                    this.localMainTopic = this.mainTopic as MainTopic;
                    if (this.localMainTopic.imageUrl && !this.localMainTopic.imageUrl.includes('/assets/')) {
                        let url = await fileService.loadImageUrl(this.localMainTopic.imageUrl)
                        this.localMainTopic.actualImageUrl = url ? url : airline;
                    } else if (this.localMainTopic.imageUrl) {
                        this.localMainTopic.actualImageUrl = this.localMainTopic.imageUrl;
                    } else {
                        this.localMainTopic.actualImageUrl = airline;
                    }
                    this.subTopicOptions = this.localMainTopic.subTopics as SubTopic[];
                }
            },
            deep: true
        },
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        dialogShow: {
            get() {
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        }
    },
    methods: {
        async getSubTopics() {
            try {
                if (this.localMainTopic) {
                    let response = await complaintService.getAllSubTopics(this.localMainTopic.id.toString());
                    if (response) {
                        this.subTopicOptions = response.data as SubTopic[];
                    }
                }
            } catch (e) {
                throw e;
            }
        },
        confirm() {
            this.dialogShow = false;
            this.$emit('onConfirm')
        },
        onSubTopicSelected() {
            const selected = this.subTopicOptions.find(o => o.id === this.subTopic);
            if (selected) {
                this.subTopicDescription = selected.formInfo.guidelines;
                this.$emit('updateSubTopicSelectedId', this.subTopic);
            }
        },
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="90%" min-height="58%" max-height="90%" :close-on-back="false" persistent>
        <v-card class="sub-topic-dialog h-100 px-3">
            <v-card-text class="pb-0">
                <v-row class="dialog-header" style="margin: 0;">
                    <v-col class="header-title" cols="12">
                        <v-card v-if="$vuetify.display.smAndUp" class="mx-auto main-topic-card" :image="localMainTopic.actualImageUrl">
                            <v-card-item style="padding-top:150px">
                                <v-card-title>
                                    {{ lang === en ? localMainTopic.topicEn : localMainTopic.topicTh }}
                                </v-card-title>
                                <v-divider></v-divider>
                                <v-card-subtitle>
                                    {{ lang === en ? localMainTopic.descriptionEn : localMainTopic.descriptionTh }}
                                </v-card-subtitle>
                            </v-card-item>
                        </v-card>
                        <v-toolbar v-if="$vuetify.display.xs" density="compact">
                            <v-toolbar-title>
                                {{ lang === en ? localMainTopic.topicEn : localMainTopic.topicTh }}
                            </v-toolbar-title>
                        </v-toolbar>
                    </v-col>
                    <!-- <v-divider v-if="$vuetify.display.xs"></v-divider> -->
                    <v-col cols="12" class="app-form-input">
                        <div class="text-label">{{ $t('t-complaint-choose-sub-topic') }}</div>
                        <v-select v-model="subTopic" class="hide-float-label" variant="outlined"
                            :label="$t('t-please-select')" hide-details="auto" :items="subTopicOptions"
                            density="comfortable" @update:model-value="onSubTopicSelected" item-value="id"
                            :rules="[rules.required(`${$t('t-please-select')}${$t('t-complaint-choose-sub-topic')}`)]">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" :value="item.raw.id"
                                    :title="lang === en ? item.raw.topicEn : item.raw.topicTh"></v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                {{ lang === en ? item.raw.topicEn : item.raw.topicTh }}
                            </template>
                        </v-select>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-text class="pt-0 guideline-content" style="height: 40vh; position: relative;">
                <v-row class="h-100" style="width: 96%; position: absolute; margin: 0;">
                    <v-col cols="12" class="h-100">
                        <v-card v-if="subTopicDescription" class="inner-card advise-card h-100">
                            <v-toolbar density="compact">
                                <v-toolbar-title>{{ $t('t-complaint-guidelines') }}</v-toolbar-title>
                            </v-toolbar>
                            <v-divider></v-divider>
                            <v-card-text class="formatted" style="height: 85%; overflow-y: auto;">
                                <div v-html="subTopicDescription"></div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <div class="w-100 d-flex justify-space-between mx-6 px-1 mb-4">
                    <v-btn class="mr-2 prev" variant="outlined" color="primary" prepend-icon="mdi mdi-arrow-left"
                        @click="$emit('updateSubTopicSelectedId', undefined); dialogShow = false" size="large">
                        {{ $t('t-back') }}</v-btn>
                    <v-btn @click="confirm" variant="flat" color="primary" size="large"
                        append-icon="mdi mdi-chevron-right" :disabled="!subTopic">
                        {{ $t('t-complaint-understand-next') }}
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>